/**
 * WidgetGrid - Responsive grid layout for widgets using react-grid-layout
 *
 * To prevent "jiggling" (infinite layout feedback loops), layout changes are
 * tracked in a ref and only persisted to context on drag/resize stop or unmount.
 * The `layouts` prop only recomputes when the widget list changes (add/remove),
 * NOT on every layout adjustment.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import type { Layout, LayoutItem, ResponsiveLayouts } from 'react-grid-layout';
import { WidgetRenderer } from './WidgetRenderer';
import { useWindows } from '../../contexts/WindowsContext';
import type { Window, WidgetLayout } from '../../types/widgets';
import { DEFAULT_WIDGET_SIZES } from '../../types/widgets';

const BREAKPOINTS = { lg: 1200, md: 768, sm: 480, xs: 0 } as const;
const COLS = { lg: 12, md: 8, sm: 4, xs: 2 } as const;

type GridBreakpoint = keyof typeof BREAKPOINTS;

function toWidgetLayouts(layout: Layout): WidgetLayout[] {
  return layout.map((l) => ({
    i: l.i,
    x: l.x,
    y: l.y,
    w: l.w,
    h: l.h,
    minW: l.minW,
    minH: l.minH,
    maxW: l.maxW,
    maxH: l.maxH,
  }));
}

interface WidgetGridProps {
  window: Window;
}

export function WidgetGrid({ window: win }: WidgetGridProps) {
  const { updateResponsiveLayouts, isEditing } = useWindows();
  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 });

  // --- Break the feedback loop ---
  // Layout changes are stored in a ref (no re-render) and only flushed to
  // context/localStorage on drag stop, resize stop, window switch, or unmount.
  const latestLayoutsRef = useRef<Record<string, WidgetLayout[]>>({});

  // Stable string that changes only when widgets are added or removed.
  const widgetIds = useMemo(
    () => win.widgets.map((w) => w.id).sort().join(','),
    [win.widgets]
  );

  // Build responsive layouts from saved state.
  // ONLY recomputes when the widget list changes — not on layout adjustments.
  // This is what prevents the render loop.
  const responsiveLayouts = useMemo((): ResponsiveLayouts<GridBreakpoint> => {
    const widgetTypeMap = new Map(win.widgets.map((w) => [w.id, w.type]));
    const widgetIdSet = new Set(win.widgets.map((w) => w.id));

    const clampItem = (l: WidgetLayout, cols: number): LayoutItem => {
      const wType = widgetTypeMap.get(l.i);
      const defaults = wType ? DEFAULT_WIDGET_SIZES[wType] : undefined;
      const minW = Math.min(defaults?.minW ?? l.minW ?? 1, cols);
      const w = Math.min(l.w, cols);
      return {
        i: l.i,
        x: Math.min(l.x, Math.max(0, cols - w)),
        y: l.y,
        w,
        h: l.h,
        minW,
        minH: defaults?.minH ?? l.minH,
        maxW: l.maxW,
        maxH: l.maxH,
      };
    };

    const result: Partial<Record<GridBreakpoint, LayoutItem[]>> = {};

    // Load saved per-breakpoint layouts
    if (win.responsiveLayouts) {
      for (const bp of Object.keys(BREAKPOINTS) as GridBreakpoint[]) {
        const saved = win.responsiveLayouts[bp];
        if (saved) {
          result[bp] = saved.map((l) => clampItem(l, COLS[bp]));
        }
      }
    }

    // Ensure lg is always present (backward compat with pre-responsive data)
    if (!result.lg) {
      result.lg = win.layouts.map((l) => clampItem(l, COLS.lg));
    }

    // Reconcile: add entries for newly added widgets, remove stale entries
    for (const bp of Object.keys(result) as GridBreakpoint[]) {
      const layout = result[bp]!;
      const existingIds = new Set(layout.map((l) => l.i));
      const cols = COLS[bp];

      for (const widget of win.widgets) {
        if (!existingIds.has(widget.id)) {
          const defaults = DEFAULT_WIDGET_SIZES[widget.type];
          const maxY = layout.reduce((max, l) => Math.max(max, l.y + l.h), 0);
          layout.push({
            i: widget.id,
            x: 0,
            y: maxY,
            w: Math.min(defaults.w, cols),
            h: defaults.h,
            minW: Math.min(defaults.minW, cols),
            minH: defaults.minH,
          });
        }
      }

      result[bp] = layout.filter((l) => widgetIdSet.has(l.i));
    }

    return result as ResponsiveLayouts<GridBreakpoint>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetIds]); // Intentionally: only recompute when widgets are added/removed

  // Track every layout change in the ref — NO state update, NO re-render.
  const handleLayoutChange = useCallback(
    (_currentLayout: Layout, allLayouts: ResponsiveLayouts<GridBreakpoint>) => {
      const converted: Record<string, WidgetLayout[]> = {};
      for (const [bp, layout] of Object.entries(allLayouts)) {
        if (layout) {
          converted[bp] = toWidgetLayouts(layout);
        }
      }
      latestLayoutsRef.current = converted;
    },
    []
  );

  // Flush ref contents to context (triggers localStorage save).
  const persistRef = useRef<() => void>(() => {});
  persistRef.current = useCallback(() => {
    if (Object.keys(latestLayoutsRef.current).length > 0) {
      updateResponsiveLayouts(win.id, latestLayoutsRef.current);
    }
  }, [win.id, updateResponsiveLayouts]);

  // Persist when user finishes dragging or resizing.
  const handleInteractionEnd = useCallback(() => {
    persistRef.current();
  }, []);

  // Persist when switching windows (win.id changes) so we don't lose layouts
  // from the previous window that were only in the ref.
  const prevWindowIdRef = useRef(win.id);
  useEffect(() => {
    if (prevWindowIdRef.current !== win.id) {
      // Flush layouts for the previous window
      if (Object.keys(latestLayoutsRef.current).length > 0) {
        updateResponsiveLayouts(prevWindowIdRef.current, latestLayoutsRef.current);
        latestLayoutsRef.current = {};
      }
      prevWindowIdRef.current = win.id;
    }
  }, [win.id, updateResponsiveLayouts]);

  // Persist on unmount (e.g. navigating away from windows mode).
  useEffect(() => {
    return () => persistRef.current();
  }, []);

  const dragConfig = useMemo(() => ({
    enabled: true,
    handle: isEditing ? undefined : '.widget-drag-grip',
    cancel: 'button, a, input, textarea, select',
  }), [isEditing]);

  const resizeConfig = useMemo(() => ({
    enabled: true,
  }), []);

  const margin = useMemo((): Partial<Record<GridBreakpoint, readonly [number, number]>> => ({
    lg: [12, 12],
    md: [12, 12],
    sm: [8, 8],
    xs: [8, 8],
  }), []);

  if (win.widgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <p className="text-lg mb-2">No widgets yet</p>
        <p className="text-sm">Add widgets to customize this window</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full">
      {mounted && (
        <ResponsiveGridLayout<GridBreakpoint>
          className="layout"
          layouts={responsiveLayouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={40}
          width={width}
          margin={margin}
          onLayoutChange={handleLayoutChange}
          onDragStop={handleInteractionEnd}
          onResizeStop={handleInteractionEnd}
          dragConfig={dragConfig}
          resizeConfig={resizeConfig}
        >
          {win.widgets.map((widget) => (
            <div key={widget.id} className="widget-container">
              <WidgetRenderer widget={widget} windowId={win.id} />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  );
}
